import { renderUserSiteFields, setupUserSiteFields, requireUserSiteFieldsReady } from "./userSiteFields.js";
import { buildUserSettingsEdit, checkUserSettingsEdit, saveTrustedUserSettings,
  startUserAccessReview, describeUserAccessEdit, getUserSettingsEdits } from "./userAccessSettings.js";
import { destroyRegisteredControlsIn } from "../../shared/ui/controls/controlCore.js";
/**
 * @file Settings-owned user modal form helpers.
 */

import {
  appData,
} from "../../app/state/state.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import {
  setModalCloseCallback,
} from "../../shared/forms/modalShell.js";
import {
  FIELD_WRAPPER_CLASSES,
  NATIVE_CHECKBOX_CLASSES,
  NATIVE_CHECKBOX_ICON_CLASSES,
  NATIVE_CHECKBOX_LABEL_CLASSES,
  NATIVE_CHECKBOX_MARKER_CLASSES,
  NATIVE_INPUT_CLASSES,
  bindNativeFieldBehavior,
  getFieldLabelHtml,
  getNativeFieldWrapperHtml,
  getReadOnlyValueHtml,
} from "../../shared/forms/nativeFieldStyles.js";
import {
  applyBrandingVariables,
} from "../../shared/ui/brandingTokens.js";
import {
  createSelectControl,
} from "../../shared/ui/controls/selectControl.js";
import { createUserOptions } from "../../shared/ui/controls/controlOptions.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
  ADMIN_APP_BOARD_PERMISSIONS,
  COLLECTION_PERMISSION_OPTIONS,
  getNormalizedUserPermissions,
  isPermissionImplied,
  isPermissionLockedByParent,
} from "./permissionModel.js";

const userFormBaselines = new WeakMap();
const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];
const NO_MANAGER_VALUE = "none";
const MANAGER_REQUIRED_MESSAGE =
  "Select a manager or choose No manager.";

function attrValue(value = "") {
  return escapeHTML(String(value ?? ""))
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Returns whether a permission should be displayed as enabled.
 * @param {Array<string>} permissions Stored permission ids.
 * @param {string} permission Permission id to check.
 * @param {string} role User or invite role.
 * @returns {boolean} True when displayed as enabled.
 */
function hasDisplayedPermission(permissions, permission, role) {
  return permissions.includes(permission) ||
    (role === "admin" && ADMIN_APP_BOARD_PERMISSIONS.includes(permission));
}

function renderPermissionCheckbox({
  checked = false,
  disabled = false,
  label,
  lockedByParent = false,
  title = "",
  value,
}) {
  const checkedAttr = checked ? " checked" : "";
  const disabledAttr = disabled ? " disabled" : "";
  const disabledClass = disabled ? " opacity-60 cursor-not-allowed" : "";
  const titleAttr = title ? ` title="${attrValue(title)}"` : "";
  const lockAttr = lockedByParent ? ' data-initial-permission-locked-by-parent="true"' : "";
  return `
    <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} min-h-9 justify-start rounded-md px-2 py-1 transition-colors hover:bg-gray-50${disabledClass}" data-native-checkbox-label${titleAttr}>
      <input type="checkbox" name="permissions" value="${attrValue(value)}" class="${NATIVE_CHECKBOX_CLASSES}" data-admin-required-permission="true"${lockAttr}${checkedAttr}${disabledAttr}>
      <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
        <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
      </span>
      <span class="min-w-0 text-sm leading-tight text-gray-700">${escapeHTML(label)}</span>
    </label>
  `;
}

function renderBooleanCheckbox({
  checked = false,
  description = "",
  label,
  name,
}) {
  const checkedAttr = checked ? " checked" : "";
  return `
    <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} w-full items-start justify-start gap-3 rounded-md border border-gray-200 bg-white p-3" data-native-checkbox-label>
      <span class="inline-flex shrink-0 items-center pt-0.5">
        <input type="checkbox" name="${attrValue(name)}" class="${NATIVE_CHECKBOX_CLASSES}"${checkedAttr}>
        <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
          <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
        </span>
      </span>
      <span class="min-w-0">
        <span class="block text-sm font-medium text-gray-700">${escapeHTML(label)}</span>
        ${description ? `<span class="mt-1 block text-xs leading-5 text-gray-500">${escapeHTML(description)}</span>` : ""}
      </span>
    </label>
  `;
}

function renderPermissionGroup(permissions, role) {
  const storedPermissions = Array.isArray(permissions) ? permissions : [];
  return `
    <fieldset class="${FIELD_WRAPPER_CLASSES}">
      <legend class="text-base text-gray-500 sm:text-sm">Collection Permissions</legend>
      <div class="grid grid-cols-1 gap-2 rounded-md border border-gray-200 bg-white p-2 sm:grid-cols-2" data-permission-grid>
        ${COLLECTION_PERMISSION_OPTIONS.map((option) => {
          const adminRequired = role === "admin" &&
            ADMIN_APP_BOARD_PERMISSIONS.includes(option.value);
          const lockedByParent = isPermissionLockedByParent(
            storedPermissions,
            option.value,
          );
          return renderPermissionCheckbox({
            checked: hasDisplayedPermission(
              storedPermissions,
              option.value,
              role,
            ),
            disabled: adminRequired || lockedByParent,
            label: option.label,
            lockedByParent,
            title: adminRequired ?
              "Admin users always keep all collection permissions." :
              lockedByParent ?
                `${option.label} is included while ${option.impliedBy} is selected.` :
                "",
            value: option.value,
          });
        }).join("")}
      </div>
    </fieldset>
  `;
}

function renderRoleManagerRow(role, managerUserId = "") {
  return `
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      ${getNativeFieldWrapperHtml(
        "Role",
        `<div data-user-role-control data-role-value="${attrValue(role)}"></div>`,
      )}
      ${getNativeFieldWrapperHtml(
        "Manager",
        `<div data-user-manager-control data-manager-value="${attrValue(managerUserId)}"></div>`,
        { required: true },
      )}
    </div>
  `;
}

function syncAdminPermissionState(scope) {
  const roleInput = scope.querySelector('input[name="role"][data-control-mirror]');
  const isAdmin = roleInput?.value === "admin";
  const selectedPermissions = Array.from(
    scope.querySelectorAll('input[name="permissions"]:checked'),
  ).map((checkbox) => checkbox.value);

  scope.querySelectorAll("[data-admin-required-permission]").forEach((checkbox) => {
    if (!(checkbox instanceof HTMLInputElement)) {return;}
    const option = COLLECTION_PERMISSION_OPTIONS.find(
      (entry) => entry.value === checkbox.value,
    );
    const initiallyLockedByParent =
      checkbox.dataset.initialPermissionLockedByParent === "true";
    const lockedByParent = !isAdmin && initiallyLockedByParent &&
      isPermissionImplied(selectedPermissions, checkbox.value);
    checkbox.checked = isAdmin || checkbox.checked;
    checkbox.disabled = Boolean(isAdmin || lockedByParent);
    const label = checkbox.closest("[data-native-checkbox-label]");
    label?.classList.toggle("opacity-60", Boolean(isAdmin || lockedByParent));
    label?.classList.toggle("cursor-not-allowed", Boolean(isAdmin || lockedByParent));
    label?.classList.toggle("cursor-pointer", !isAdmin && !lockedByParent);
    if (isAdmin) {
      label?.setAttribute("title", "Admin users always keep all collection permissions.");
    } else if (lockedByParent) {
      label?.setAttribute(
        "title",
        `${option?.label || checkbox.value} is included while ${option?.impliedBy || "the parent permission"} is selected.`,
      );
    } else {
      label?.removeAttribute("title");
    }
  });
  bindNativeFieldBehavior(scope);
}

function setupRoleControl(scope) {
  const roleRoot = scope.querySelector("[data-user-role-control]");
  if (!roleRoot) {return;}

  createSelectControl({
    root: roleRoot,
    name: "role",
    value: roleRoot.dataset.roleValue || "user",
    options: ROLE_OPTIONS,
    placeholder: "Select role",
    accessibleName: "Role",
    clearable: false,
    onChange: () => syncAdminPermissionState(scope),
  });
}

function getManagerOptions(selectedValue = "") {
  const userOptions = createUserOptions(appData.users || [], {
    selectedValues: selectedValue && selectedValue !== NO_MANAGER_VALUE ?
      [selectedValue] :
      [],
  }).sort((optionA, optionB) => optionA.label.localeCompare(optionB.label));

  return [
    {
      value: NO_MANAGER_VALUE,
      label: "No manager",
      group: "Manager",
      order: 0,
    },
    ...userOptions,
  ];
}

function setupManagerControl(scope) {
  const managerRoot = scope.querySelector("[data-user-manager-control]");
  if (!managerRoot) {return;}

  createSelectControl({
    root: managerRoot,
    name: "managerUserId",
    value: managerRoot.dataset.managerValue || "",
    options: getManagerOptions(managerRoot.dataset.managerValue),
    placeholder: "+ Add Manager",
    accessibleName: "Manager",
    clearable: false,
    required: true,
    requiredMessage: MANAGER_REQUIRED_MESSAGE,
    searchable: true,
  });
}

function applyUserInviteModalSize(scope) {
  if (scope?.dataset?.type !== "user_invite") {return;}
  const panel = scope.closest("#form-modal")?.firstElementChild;
  if (!panel) {return;}

  panel.classList.remove("max-w-2xl");
  panel.classList.add("max-w-lg", "settings-user-invite-modal");
  setModalCloseCallback(() => {
    panel.classList.remove("max-w-lg", "settings-user-invite-modal");
    panel.classList.add("max-w-2xl");
  });
}

function setupUserModalControls(scope) {
  if (!scope) {return;}
  const modal = scope.closest("#form-modal") || scope;
  applyBrandingVariables(modal, appData);
  applyUserInviteModalSize(scope);
  setupRoleControl(scope);
  setupManagerControl(scope);
  bindNativeFieldBehavior(scope);
  syncAdminPermissionState(scope);
  scope.querySelector("[data-user-role-control]")?.addEventListener("control:valuechange", () => {
    syncAdminPermissionState(scope);
  });
  scope.querySelector("[data-permission-grid]")?.addEventListener("change", () => {
    syncAdminPermissionState(scope);
  });
}

/**
 * Saves the Settings user edit form.
 * @param {object} params Submit context.
 * @param {object} params.data Form payload.
 * @param {FormData} params.formData Raw form data.
 * @param {HTMLFormElement} params.form Source form.
 * @param {string} params.id User ID.
 * @returns {Promise<boolean>} True when saved; false while review is pending.
 */
export async function saveUserForm({ data, formData, form, id }) {
  data.permissions = getNormalizedUserPermissions(
    data.role,
    Array.from(formData.getAll("permissions")),
  );
  if (!data.managerUserId) {
    throw new Error(MANAGER_REQUIRED_MESSAGE);
  }
  data.isAppProvider = form.elements.isAppProvider?.checked === true;
  requireUserSiteFieldsReady(form);

  if (!id) {
    openConfirmationModal(
      "Security Restriction",
      "Creating users from the client is disabled for security.",
      async () => {},
      false,
      "OK",
    );
    throw new Error("Client-side user creation is disabled.");
  }

  const original = userFormBaselines.get(form);
  if (!original) {throw new Error("Reopen the user form before saving.");}
  const edit = buildUserSettingsEdit(original, data);
  if (!edit) {return true;}
  const impact = await checkUserSettingsEdit(edit);
  if (impact.summary.rowCount) {
    showAccessReviewFormStep(form, edit, impact);
    return false;
  }
  await saveTrustedUserSettings(edit);
  return true;
}

/**
 * Opens the Settings user edit modal.
 * @param {string|null} [userId=null] User id to edit.
 */
export function openUserForm(userId = null) {
  const user = (appData.users || []).find((entry) => entry.id === userId);
  if (!user) {throw new Error("Cannot edit user: user not found.");}
  if (getUserSettingsEdits().some((edit) => edit.targetUserId === userId)) {
    openConfirmationModal("Unsaved user changes",
      "Save or cancel this user's pending Settings changes before opening Edit User.",
      () => {}, false, "OK");
    return;
  }
  openStandardForm("user", user, {
    getFieldsHtml: (record) => getEditUserFormFields(record, record.permissions),
    afterRender: ({itemForm}) => {
      userFormBaselines.set(itemForm, structuredClone(user));
      setupUserModalControls(itemForm);
      setupUserSiteFields(itemForm, user, appData.config?.defaultSiteId);
      if (user.isActive === false) {
        itemForm.querySelectorAll("input, button[type='submit']")
          .forEach((control) => {control.disabled = true;});
      }
    },
  });
}

/**
 * Presents impact in the current form, preserving fields for Back to editing.
 * @param {HTMLFormElement} form Source form.
 * @param {object} edit Pending edit.
 * @param {object} impact Current server impact.
 * @returns {void}
 */
function showAccessReviewFormStep(form, edit, impact) {
  const fields = form.querySelector("#form-fields");
  const children = [...fields.children];
  children.forEach((child) => {child.hidden = true;});
  const previousReview = form.querySelector("[data-user-access-review]");
  if (previousReview) {
    destroyRegisteredControlsIn(previousReview);
    previousReview.remove();
  }
  const review = document.createElement("div");
  review.className = "space-y-4";
  review.dataset.userAccessReview = "true";
  review.innerHTML = `<h3 tabindex="-1" class="text-lg font-semibold text-gray-900">Review access changes</h3>
    <p class="text-sm text-gray-600">${escapeHTML(describeUserAccessEdit(edit))}.</p>
    <p class="text-sm text-gray-600">${impact.summary.rowCount} affected work items need review. The user's access has not changed.</p>
    <div class="flex flex-wrap gap-3">
      <button type="button" class="settings-primary-action-btn" data-review-work>Review affected work</button>
      <button type="button" class="settings-neutral-action-btn" data-back-to-edit>Back to editing</button>
    </div><p role="alert" class="text-sm text-red-600" data-review-error></p>`;
  fields.append(review);
  const submit = form.querySelector("button[type='submit']");
  if (submit) {submit.hidden = true;}
  review.querySelector("h3").focus();
  review.querySelector("[data-back-to-edit]").onclick = () => {
    review.remove();
    children.forEach((child) => {child.hidden = false;});
    if (submit) {submit.hidden = false;}
    form.querySelector("input[name='name']")?.focus();
  };
  review.querySelector("[data-review-work]").onclick = async (event) => {
    event.currentTarget.disabled = true;
    try {await startUserAccessReview(edit);} catch (error) {
      review.querySelector("[data-review-error]").textContent = error.message;
      review.querySelector("[data-review-work]").disabled = false;
    }
  };
}

/**
 * Generates HTML for the Edit User form.
 * @param {object} user User record.
 * @param {Array<string>} userPerms Current collection permissions.
 * @returns {string} Edit user form markup.
 */
export function getEditUserFormFields(user, userPerms) {
  const role = user?.role || "user";
  return `
    <div class="space-y-4">
      ${getNativeFieldWrapperHtml(
        "Name",
        `<input type="text" name="name" value="${attrValue(user?.name)}" class="${NATIVE_INPUT_CLASSES}" required>`,
        { required: true },
      )}
      ${getNativeFieldWrapperHtml(
        "Email",
        getReadOnlyValueHtml(escapeHTML(user?.email || "Not set")),
      )}
      ${renderRoleManagerRow(role, user?.managerUserId || "")}
      ${renderUserSiteFields()}
      ${renderBooleanCheckbox({
        checked: user?.isAppProvider === true,
        description: "Enables this user for the main app provider model. This does not change drugs provider access.",
        label: "App Provider",
        name: "isAppProvider",
      })}
      ${renderPermissionGroup(userPerms, role)}
    </div>`;
}

/**
 * Generates HTML for the user invite create/edit form.
 * @param {object} invite Invite data for editing.
 * @returns {string} Invite form markup.
 */
export function getUserInviteFormFields(invite) {
  const invitePerms = invite?.permissions || [];
  const role = invite?.role || "user";

  return `
    <div class="space-y-4">
      <div class="${FIELD_WRAPPER_CLASSES}">
        ${getFieldLabelHtml("Email", true)}
        <input type="email" name="email" value="${attrValue(invite?.email)}" class="${NATIVE_INPUT_CLASSES}" required>
        <p class="mt-1 text-xs leading-5 text-gray-500" data-native-field-skip="true">Email must be the same email to be used for sign in.</p>
      </div>
      ${getNativeFieldWrapperHtml(
        "Provisional Name",
        `<input type="text" name="name" value="${attrValue(invite?.name)}" class="${NATIVE_INPUT_CLASSES}" placeholder="Optional display name for the invite email">`,
      )}
      ${renderRoleManagerRow(role, invite?.managerUserId || "")}
      ${renderUserSiteFields()}
      ${renderBooleanCheckbox({
        checked: invite?.isAppProvider === true,
        label: "App Provider",
        name: "isAppProvider",
      })}
      ${renderPermissionGroup(invitePerms, role)}
    </div>`;
}

/**
 * Opens the user invite modal.
 * @param {object | null} invite Existing invite or initial data.
 * @returns {void}
 */
export function openUserInviteForm(invite = null) {
  openStandardForm("user_invite", invite, {
    getFieldsHtml: getUserInviteFormFields,
    afterRender: ({ itemForm }) => {
      setupUserModalControls(itemForm);
      setupUserSiteFields(itemForm, invite || {}, appData.config?.defaultSiteId);
    },
    useInviteActions: true,
  });
}
